import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SwipeableListItem } from '../../../../../../generic/swipe/SwipeableListAdapter';
import { SwipeAction } from '../../../../../../generic/ui/SwipeAction';
import { Mono } from '../../../../../../generic/ui/Mono';
import TrashIcon from '../../../../../../generic/icons/IconLib/TrashIcon';
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';

interface Props {
    item: any;
    isLast?: boolean;
    deleteFromPallet: (id: number, item: any) => void;
}

const LikutExistGoodsRow: React.FC<Props> = ({ item, isLast = false, deleteFromPallet }) => {
    const [t] = useTranslation(['likut']);
    const { ProductDesc, VarietyDesc, Pack_Name, WeightNeto, Quantity, RecID } = item;
    const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState(false);

    return (
        <SwipeShell $isLast={isLast}>
            <SwipeableListItem
                swipeRight={{
                    content: (
                        <SwipeAction
                            variant='delete'
                            align='start'
                            icon={<TrashIcon color='currentColor' size={16} />}
                        />
                    ),
                    action: () => {
                        setIsOpenConfirmDelete(!isOpenConfirmDelete);
                    },
                }}
                threshold={0.35}
            >
                <ConfirmDeleteBtn
                    $visible={isOpenConfirmDelete}
                    onClick={() => {
                        deleteFromPallet(RecID, item);
                    }}
                    aria-label={t('likut_sectionmain_product_swiperight_button')}
                >
                    <TrashIcon color='currentColor' size={16} />
                </ConfirmDeleteBtn>
                <Card>
                    <ContentColumn>
                        <TitleText>
                            {ProductDesc?.trim()} {VarietyDesc?.trim()} {Pack_Name?.trim()}
                        </TitleText>
                        <MetricsLine>
                            <MetricItem>
                                <IconSlot>
                                    <ScaleIcon color='currentColor' size={14} />
                                </IconSlot>
                                <MetricLabel>{t('likut_sectionmain_pallet_product_weight')}</MetricLabel>
                                <MetricValue>{WeightNeto}</MetricValue>
                                {/* <UnitLabel>ק"ג</UnitLabel> */}
                            </MetricItem>
                            <MetricItem>
                                <IconSlot>
                                    <PackageBoxIcon color='currentColor' size={14} />
                                </IconSlot>
                                <MetricLabel>{t('likut_sectionmain_pallet_product_qty')}</MetricLabel>
                                <MetricValue>{Quantity}</MetricValue>
                                {/* <UnitLabel>יח</UnitLabel> */}
                            </MetricItem>
                        </MetricsLine>
                    </ContentColumn>
                </Card>
            </SwipeableListItem>
        </SwipeShell>
    );
};

export default LikutExistGoodsRow;

const SwipeShell = styled.div<{ $isLast: boolean }>`
    width: 100%;
    overflow-x: hidden;
    position: relative;
    border-bottom: ${({ theme, $isLast }) => ($isLast ? 'none' : `1px solid ${theme.colors.line2}`)};
`;

const ConfirmDeleteBtn = styled.button<{ $visible: boolean }>`
    display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
    align-items: center;
    justify-content: center;
    min-width: 4rem;
    border: 0;
    background: ${({ theme }) => theme.colors.danger};
    color: #ffffff;
    cursor: pointer;
`;

const Card = styled.div`
    width: 100%;
    min-height: 3.5rem;
    display: flex;
    align-items: center;
    direction: rtl;
    padding: 0.5rem 0.875rem;
    background: ${({ theme }) => theme.colors.bg};
    opacity: 0.75;
`;

const ContentColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const TitleText = styled.span`
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink2};
    font-family: ${({ theme }) => theme.fonts.sans};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const MetricsLine = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`;

const MetricItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink4};
    flex-shrink: 0;
`;

const MetricLabel = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
`;

const MetricValue = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink3};
`;

const UnitLabel = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    font-family: ${({ theme }) => theme.fonts.sans};
`;
